// frontend/src/pages/purchase/ProductList.jsx

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { getSuppliers } from '../../utils/supplierApi';
import { getCategories } from '../../utils/categoryApi';
import { getProducts, createBasicProduct } from '../../utils/productApi';

function ProductList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [largeCategories, setLargeCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // ページネーション用
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // 検索フィルター
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategoryTab, setSelectedCategoryTab] = useState('');

  // モーダル
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 【前回修正】初期の仕入先コードを「01」へ変更
  const [supplierCode, setSupplierCode] = useState('01');
  const [selectedLargeUuid, setSelectedLargeUuid] = useState('');
  const [filteredMidsForModal, setFilteredMidsForModal] = useState([]);

  const [newProduct, setNewProduct] = useState({
    model_code: '', name: '', internal_category_uuid: '', barcode_type: 'single', brand: '', season: '2026SS', note: ''
  });

  useEffect(() => {
    fetchMainProducts();
  }, [searchKeyword, selectedCategoryTab, currentPage, perPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchKeyword, selectedCategoryTab]);

  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const [sData, cData] = await Promise.all([getSuppliers(), getCategories()]);
        setSuppliers(sData);
        setAllCategories(cData);
        setLargeCategories(cData.filter(c => !c.parent_uuid));
      } catch (e) {
        console.error('マスターデータの読込に失敗しました', e);
      }
    };
    loadMasterData();
  }, []);

  const fetchMainProducts = async () => {
    setLoading(true);
    try {
      const data = await getProducts(searchKeyword, selectedCategoryTab, currentPage, perPage);
      setProducts(data.items || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const supplierOptions = suppliers.map(s => ({
    value: s.supplier_code || s.code || '', label: `${s.supplier_code || s.code || ''} : ${s.name}`
  }));
  const largeOptions = largeCategories.map(c => ({ value: c.uuid, label: `${c.code} : ${c.name}` }));
  const midOptions = filteredMidsForModal.map(c => ({ value: c.uuid, label: `${c.code} : ${c.name}` }));

  const handleLargeCategoryChange = (selectedOption) => {
    const largeUuid = selectedOption ? selectedOption.value : '';
    setSelectedLargeUuid(largeUuid);
    if (!largeUuid) {
      setFilteredMidsForModal([]);
      setNewProduct(prev => ({ ...prev, internal_category_uuid: '' }));
      return;
    }
    const mids = allCategories.filter(c => c.parent_uuid === largeUuid);
    setFilteredMidsForModal(mids);
    if (mids.length > 0) {
      setNewProduct(prev => ({ ...prev, internal_category_uuid: mids[0].uuid }));
    } else {
      setNewProduct(prev => ({ ...prev, internal_category_uuid: '' }));
    }
  };

  const handleCreateBasicProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.internal_category_uuid) return alert('中分類を選択してください。');
    try {
      const payload = { ...newProduct, variants: [] };
      const created = await createBasicProduct(payload, supplierCode);
      setIsModalOpen(false);
      navigate(`/purchase/products/edit/${created.uuid}`);
    } catch (error) {
      alert(error.message);
    }
  };

  const totalPages = Math.ceil(totalCount / perPage) || 1;
  const startItem = (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, totalCount);

  const getVisiblePages = () => {
    const pages = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + 4);
    const adjustedStart = Math.max(1, Math.min(start, totalPages - 4));
    for (let i = adjustedStart; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div>
      {/* ヘッダー */}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item">仕入・在庫管理</li>
            <li className="breadcrumb-item active">商品マスター一覧</li>
          </ol>
          <h1 className="page-header mb-0">商品マスター管理 <small>({totalCount} 件の商品)</small></h1>
        </div>
        <div className="ms-auto">
          <button onClick={() => { setIsModalOpen(true); setSelectedLargeUuid(''); setFilteredMidsForModal([]); }} className="btn btn-success btn-rounded px-4 rounded-pill fw-bold">
            <i className="fa fa-plus me-2"></i> 新規商品を追加
          </button>
        </div>
      </div>

      {/* カテゴリタブ */}
      <div className="card border-0 shadow-sm">
        <ul className="nav nav-tabs nav-tabs-v2 px-3">
          <li className="nav-item me-2">
            <button className={`nav-link px-3 py-2 fw-bold ${selectedCategoryTab === '' ? 'active' : ''}`} onClick={() => setSelectedCategoryTab('')}>
              📦 すべての商品
            </button>
          </li>
          {largeCategories.map(cat => (
            <li className="nav-item me-2" key={cat.uuid}>
              <button className={`nav-link px-3 py-2 fw-bold ${selectedCategoryTab === cat.uuid ? 'active text-primary' : ''}`} onClick={() => setSelectedCategoryTab(cat.uuid)}>
                {cat.name}
              </button>
            </li>
          ))}
        </ul>

        <div className="tab-content p-3">
          <div className="input-group mb-3 shadow-sm">
            <span className="input-group-text bg-light border-end-0"><i className="fa fa-search opacity-6"></i></span>
            <input type="text" className="form-control form-control-lg bg-light border-start-0 fs-15px" placeholder="品番（自社型番）、または商品名でリアルタイム検索..." value={searchKeyword} onChange={(e) => setSearchKeyword(e.target.value)} />
          </div>

          {loading ? <p className="text-center py-4 text-muted"><i className="fa fa-spinner fa-spin me-2"></i>読込中...</p> : (
            <>
              <div className="table-responsive">
                <table className="table table-hover align-middle fw-bold mb-0">
                  <thead>
                    <tr className="table-light text-muted small">
                      <th width="1%"></th>
                      <th width="42%">商品タイトル / ブランド</th>
                      <th width="20%">自社型番 (品番)</th>
                      <th width="18%">自社共通分類</th>
                      <th width="10%">展開SKU</th>
                      <th width="9%" className="text-center">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 ? (
                      <tr><td colSpan="6" className="text-center text-muted py-4">該当する商品が見つかりません。</td></tr>
                    ) : (
                      products.map((product) => {
                        const realImgUrl = product.image_url || product.main_image_url || (product.variants && product.variants[0]?.variant_image_url);

                        return (
                          <tr key={product.uuid}>
                            <td><input type="checkbox" className="form-check-input" /></td>
                            <td>
                              <div className="d-flex align-items-center">
                                {/* 🌟 プレースホルダー枠（水平・垂直ともに完璧に中央配置に修正） */}
                                <div className="bg-light border overflow-hidden shadow-sm rounded" style={{ width: '55px', height: '55px', minWidth: '55px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {realImgUrl ? (
                                    <img src={realImgUrl} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  ) : (
                                    /* 🌟 アイコンが真ん中に配置されるようすっきり修正 */
                                    <i className="fa fa-image fs-4 text-muted opacity-3"></i>
                                  )}
                                </div>
                                <div className="ms-3" style={{ maxWidth: '280px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  <Link to={`/purchase/products/edit/${product.uuid}`} className="text-dark fw-bold text-decoration-none hover-primary line-height-sm d-block">
                                    {product.name}
                                  </Link>
                                  <div className="text-muted small fw-normal mt-1">{product.brand || 'No Brand'}</div>
                                </div>
                              </div>
                            </td>
                            <td className="fw-bold text-primary fs-14px font-monospace">{product.model_code}</td>
                            <td><span className="badge bg-light text-dark border px-2 py-1 fw-bold">{product.internal_category?.name || '未分類'}</span></td>
                            <td><span className="badge bg-indigo bg-opacity-10 text-indigo px-2 py-1 fs-12px">{product.variants?.length || 0} SKU</span></td>
                            <td className="text-center">
                              <Link to={`/purchase/products/edit/${product.uuid}`} className="btn btn-sm btn-white fw-bold border shadow-sm text-nowrap"><i className="fa fa-edit me-1 text-primary"></i>管理</Link>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* 【前回修正】2重線を削除 */}
              {products.length > 0 && (
                <div className="d-flex align-items-center justify-content-between pt-3 mt-2 px-2">
                  <div className="d-flex align-items-center text-muted small fw-bold">
                    <span className="me-3">全 {totalCount} 件中 {startItem} 〜 {endItem} 件を表示</span>
                    <select className="form-select form-select-sm fw-bold border bg-light" style={{ width: '110px' }} value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setCurrentPage(1); }}>
                      <option value="10">10件ずつ</option>
                      <option value="20">20件ずつ</option>
                      <option value="50">50件ずつ</option>
                    </select>
                  </div>

                  <ul className="pagination pagination-sm mb-0 fw-bold">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} title="最初のページへ">
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                        前へ
                      </button>
                    </li>

                    {getVisiblePages().map(pageNum => (
                      <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                        <button className="page-link px-3" onClick={() => setCurrentPage(pageNum)}>
                          {pageNum}
                        </button>
                      </li>
                    ))}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                        次へ
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} title="最後のページへ">
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* モーダル */}
      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="bg-white rounded shadow-lg overflow-hidden" style={{ width: '100%', maxWidth: '520px' }}>
            <div className="p-3 bg-dark text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold"><i className="fa fa-cube me-2 text-success"></i>新規商品の基本登録</h5>
              <button className="btn-close btn-close-white" onClick={() => setIsModalOpen(false)}></button>
            </div>
            <form onSubmit={handleCreateBasicProduct} className="p-4 fw-bold text-dark">
              <div className="mb-3">
                <label className="form-label text-success fw-bold"><i className="fa fa-truck me-1"></i> 仕入先 (番号または名前で検索) <span className="text-danger">*</span></label>
                <Select options={supplierOptions} value={supplierOptions.find(o => o.value === supplierCode) || null} onChange={(opt) => setSupplierCode(opt ? opt.value : '')} placeholder="-- 仕入先を選択してください --" noOptionsMessage={() => "見つかりません"} isSearchable={true} required />
              </div>
              <div className="mb-3">
                <label className="form-label">自社管理型番 (品番) <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={newProduct.model_code} onChange={(e) => setNewProduct({ ...newProduct, model_code: e.target.value })} placeholder="例: TG-OP-102" required />
              </div>
              <div className="mb-3">
                <label className="form-label">商品名 <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="例: シフォンマキシドレス" required />
              </div>
              <div className="mb-3">
                <label className="form-label text-warning"><i className="fa fa-folder-open me-1"></i> Step 1: 大分類を選択</label>
                <Select options={largeOptions} value={largeOptions.find(o => o.value === selectedLargeUuid) || null} onChange={handleLargeCategoryChange} placeholder="-- リストから検索・選択 --" noOptionsMessage={() => "カテゴリがありません"} isClearable={true} />
              </div>
              <div className="mb-4">
                <label className="form-label text-primary"><i className="fa fa-tags text-primary me-1"></i> Step 2: 中分類を選択 <span className="text-danger">*</span></label>
                <Select options={midOptions} value={midOptions.find(o => o.value === newProduct.internal_category_uuid) || null} onChange={(opt) => setNewProduct(prev => ({ ...prev, internal_category_uuid: opt ? opt.value : '' }))} placeholder={selectedLargeUuid ? "-- 中分類をリストから選択 --" : "⚠️ 先に上の大分類を選択してください"} noOptionsMessage={() => "中分類が登録されていません"} isDisabled={filteredMidsForModal.length === 0} required />
              </div>
              <div className="text-end border-top pt-3">
                <button type="button" className="btn btn-default me-2 fw-bold" onClick={() => setIsModalOpen(false)}>キャンセル</button>
                <button type="submit" className="btn btn-success px-4 fw-bold text-white shadow-sm">次へ：詳細・SKUを設定する <i className="fa fa-arrow-right ms-1"></i></button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductList;